/**
 * The app-side emit host for the clinician/practice trial-ended modal
 * (`@heidi/commerce`).
 *
 * Ruling 24: a package's `/ui` may not reach the analytics pipeline, so the
 * domain side decides WHEN each event fires, which cohort's `screen_name` it
 * reports and every `intl`-formatted label; these two functions add the
 * `event_name`, the destination routing, and nothing else.
 *
 * ROUTING IS PRESERVED, NOT TIDIED. The view keeps `{ amplitude: false }` and
 * the six clicks keep no `options` at all, which is what the modal has always
 * sent. None of this root's three event names is on the Snowplow denylist
 * (`@/lib/analytics/snowplow-suppression` — `error_viewed` and `screen_viewed`),
 * so no emit here needs an `options` object for suppression and none should
 * acquire one: the denylist is applied by event NAME inside the pipeline's
 * `enrich()`, and a `snowplow: false` on a call site would be a second spelling
 * of a policy that already covers it.
 *
 * MODULE-LEVEL FUNCTIONS ON PURPOSE. `trackClinicianTrialEndedViewed` ends up a
 * dependency of the modal's open effect, so an emit whose identity changed per
 * render would re-fire it and double-count the view. A module binding has one
 * identity for the life of the process, which is stricter than the seam requires.
 */
import { track, TrackingEventName } from '@heidi/analytics/events';
import { ButtonAction } from '@heidi/analytics/vocabulary';
import {
  CLINICIAN_TRIAL_ENDED_ACTION,
  type ClinicianTrialEndedActionToken,
  type ClinicianTrialEndedAnalyticsPorts,
  type TrackClinicianTrialEndedClick,
  type TrackClinicianTrialEndedViewed,
} from '@heidi/commerce/contracts';
import { TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN } from './trialsAndUpgradesScreenNames';

/**
 * The package's `action` tokens bound to the app enum members that declare them.
 *
 * `screen_name` is bound by the shared root map
 * (`./trialsAndUpgradesScreenNames`) because this modal's two screens are also
 * reached by the team upgrade-request host; the actions are this surface's alone,
 * so their map stays here. `trackClinicianTrialEndedAnalytics.test.ts` asserts
 * every entry maps a token to an enum member of the identical value, which is
 * what makes this a binding rather than a second spelling.
 */
const BUTTON_ACTION_BY_TOKEN = {
  [CLINICIAN_TRIAL_ENDED_ACTION.keepClinician]:
    ButtonAction.ClinicianTrialEndedKeepClinician,
  [CLINICIAN_TRIAL_ENDED_ACTION.keepPractice]:
    ButtonAction.ClinicianTrialEndedKeepPractice,
  [CLINICIAN_TRIAL_ENDED_ACTION.requestToUpgrade]:
    ButtonAction.ClinicianTrialEndedRequestToUpgrade,
  [CLINICIAN_TRIAL_ENDED_ACTION.remain]: ButtonAction.ClinicianTrialEndedRemain,
  [CLINICIAN_TRIAL_ENDED_ACTION.talkToUs]: ButtonAction.ClinicianTrialEndedTalkToUs,
  [CLINICIAN_TRIAL_ENDED_ACTION.contactSales]:
    ButtonAction.ClinicianTrialEndedContactSales,
} as const satisfies Record<ClinicianTrialEndedActionToken, ButtonAction>;

/** The modal became visible. The once-per-open guard runs in the domain hook. */
export const trackClinicianTrialEndedViewed: TrackClinicianTrialEndedViewed = ({
  screen_name,
}) => {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ModalViewed,
    props: { screen_name: TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN[screen_name] },
  });
};

/** One of the six CTAs was clicked. `label` is domain-computed and passes through. */
export const trackClinicianTrialEndedClick: TrackClinicianTrialEndedClick = ({
  screen_name,
  action,
  ...rest
}) => {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN[screen_name],
      action: BUTTON_ACTION_BY_TOKEN[action],
      ...rest,
    },
  });
};

/**
 * The two seams as one bundle.
 *
 * A module constant rather than a `useMemo`, so it is the same object on every
 * render and cannot be the thing that re-fires the open effect.
 */
export const clinicianTrialEndedAnalyticsPorts: ClinicianTrialEndedAnalyticsPorts = {
  trackViewed: trackClinicianTrialEndedViewed,
  trackClick: trackClinicianTrialEndedClick,
};
